package com.daytimegaming.ourdailyfinances.presentation.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.daytimegaming.ourdailyfinances.presentation.home.MonthlyIncomeVsExpenses
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import kotlin.math.abs
import kotlin.math.max

@Composable
fun IncomeVsExpensesChart(
    data: List<MonthlyIncomeVsExpenses>,
    modifier: Modifier = Modifier
) {
    val greenColor = Color(0xFF4CAF50)
    val redColor = Color(0xFFEF5350)
    val blueColor = Color(0xFF2196F3)
    val gridColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.1f)
    val textMeasurer = rememberTextMeasurer()

    Canvas(modifier = modifier) {
        if (data.isEmpty()) return@Canvas

        val width = size.width
        val height = size.height

        val paddingTop = 24.dp.toPx()
        val paddingBottom = 24.dp.toPx()
        val chartHeight = height - paddingTop - paddingBottom

        val maxIncome = data.maxOfOrNull { it.income } ?: 1.0
        val maxExpense = data.maxOfOrNull { it.expenses } ?: 1.0
        val maxNetPos = data.maxOfOrNull { max(0.0, it.netIncome) } ?: 0.0
        val maxNetNeg = data.maxOfOrNull { max(0.0, -it.netIncome) } ?: 0.0

        val maxUpper = max(maxIncome, maxNetPos).coerceAtLeast(100.0)
        val maxLower = max(maxExpense, maxNetNeg).coerceAtLeast(100.0)

        // Baseline Y coordinate (X-axis)
        val yZero = paddingTop + chartHeight * (maxUpper / (maxUpper + maxLower)).toFloat()

        val scaleUpper = (yZero - paddingTop) / maxUpper.toFloat()
        val scaleLower = (height - paddingBottom - yZero) / maxLower.toFloat()

        // Draw Zero Baseline (X-axis)
        drawLine(
            color = gridColor,
            start = Offset(0f, yZero),
            end = Offset(width, yZero),
            strokeWidth = 2.dp.toPx(),
            pathEffect = PathEffect.dashPathEffect(floatArrayOf(12f, 8f), 0f)
        )

        val columnCount = data.size
        val columnWidth = width / columnCount
        val barWidth = (28.dp.toPx()).coerceAtMost(columnWidth * 0.35f)

        val netPoints = mutableListOf<Offset>()

        data.forEachIndexed { i, item ->
            val xCenter = columnWidth * (i + 0.5f)

            // Draw Income Bar (Green, above X-axis)
            if (item.income > 0) {
                val barHeight = (item.income.toFloat() * scaleUpper).coerceAtLeast(4f)
                val topY = yZero - barHeight
                drawRoundRect(
                    color = greenColor,
                    topLeft = Offset(xCenter - barWidth / 2f, topY),
                    size = Size(barWidth, barHeight),
                    cornerRadius = CornerRadius(4.dp.toPx(), 4.dp.toPx())
                )

                // Draw income amount above top of income bar
                val incomeText = "+$${item.income.formatAmount()}"
                val textLayout = textMeasurer.measure(
                    text = incomeText,
                    style = TextStyle(
                        fontSize = 10.sp,
                        fontWeight = FontWeight.Bold,
                        color = greenColor
                    )
                )
                drawText(
                    textLayoutResult = textLayout,
                    topLeft = Offset(xCenter - textLayout.size.width / 2f, topY - textLayout.size.height - 2.dp.toPx())
                )
            }

            // Draw Expense Bar (Red, below X-axis)
            if (item.expenses > 0) {
                val barHeight = (item.expenses.toFloat() * scaleLower).coerceAtLeast(4f)
                val bottomY = yZero + barHeight
                drawRoundRect(
                    color = redColor,
                    topLeft = Offset(xCenter - barWidth / 2f, yZero),
                    size = Size(barWidth, barHeight),
                    cornerRadius = CornerRadius(4.dp.toPx(), 4.dp.toPx())
                )

                // Draw expense amount below bottom of expense bar
                val expenseText = "-$${item.expenses.formatAmount()}"
                val textLayout = textMeasurer.measure(
                    text = expenseText,
                    style = TextStyle(
                        fontSize = 10.sp,
                        fontWeight = FontWeight.Bold,
                        color = redColor
                    )
                )
                drawText(
                    textLayoutResult = textLayout,
                    topLeft = Offset(xCenter - textLayout.size.width / 2f, bottomY + 2.dp.toPx())
                )
            }

            // Calculate Net Income Point (Line graph)
            val netY = if (item.netIncome >= 0) {
                yZero - (item.netIncome.toFloat() * scaleUpper)
            } else {
                yZero + (abs(item.netIncome).toFloat() * scaleLower)
            }
            netPoints.add(Offset(xCenter, netY))
        }

        // Draw Net Income Line Graph connecting months
        if (netPoints.size > 1) {
            val path = Path()
            netPoints.forEachIndexed { index, point ->
                if (index == 0) {
                    path.moveTo(point.x, point.y)
                } else {
                    path.lineTo(point.x, point.y)
                }
            }
            drawPath(
                path = path,
                color = blueColor,
                style = Stroke(
                    width = 3.dp.toPx(),
                    cap = StrokeCap.Round
                )
            )

            // Draw Net Income Dots
            netPoints.forEach { point ->
                drawCircle(
                    color = blueColor,
                    radius = 5.dp.toPx(),
                    center = point
                )
                drawCircle(
                    color = Color.White,
                    radius = 2.dp.toPx(),
                    center = point
                )
            }
        }
    }
}

@Composable
fun IncomeVsExpensesLegend(
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceEvenly,
        verticalAlignment = Alignment.CenterVertically
    ) {
        IncomeVsExpensesLegendItem(
            color = Color(0xFF4CAF50),
            label = "Income"
        )
        IncomeVsExpensesLegendItem(
            color = Color(0xFFEF5350),
            label = "Expenses"
        )
        IncomeVsExpensesLegendItem(
            color = Color(0xFF2196F3),
            label = "Net Income"
        )
    }
}

@Composable
fun IncomeVsExpensesLegendItem(
    color: Color,
    label: String,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier,
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        Box(
            modifier = Modifier
                .size(10.dp)
                .clip(RoundedCornerShape(2.dp))
                .background(color)
        )
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}
